import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import dayjs from 'dayjs';
import { changePassword, fetchUser, updateUser } from '../userSlice';
import {
  AuthUser,
  FetchUserAttributesOutput,
  UpdatePasswordInput,
  UpdateUserAttributesInput,
  UpdateUserAttributesOutput,
} from 'aws-amplify/auth';

export interface Account {
  email: string;
  firstName: string;
  lastName: string;
}

export interface AccountModalState {
  stateIsChanged: boolean;
  isOpenAccountModal: boolean;
  accountIdToEdit?: string;
  deleteAccountModal: boolean;
  loading: boolean;
  disableButtons: boolean;
  updateCallback?: (user: undefined) => Promise<void>;
  formData: Account;
}

const initialFormData: Account = {
  email: '',
  firstName: '',
  lastName: '',
};

// Initialize state
const initialState: AccountModalState = {
  stateIsChanged: false,
  isOpenAccountModal: false,
  accountIdToEdit: undefined,
  deleteAccountModal: false,
  loading: false,
  disableButtons: false,
  formData: initialFormData,
};

export const accountModalSlice = createSlice({
  name: 'accountModal',
  initialState,
  reducers: {
    openAccountModal: (
      state,
      action: PayloadAction<
        | {
            createCallback?: (newAccount: Account | undefined) => Promise<void>;
            resetState?: boolean;
          }
        | undefined
      >,
    ) => {
      // If resetState is true, reset the formData and any other relevant fields
      if (action.payload?.resetState) {
        return {
          ...initialState,
          isOpenAccountModal: true,
          createCallback: action.payload.createCallback,
          formData: {
            ...initialFormData,
          }, // Reset form data back to initial
        };
      }

      // Otherwise, open the modal without resetting
      return {
        ...state,
        isOpenAccountModal: true,
        createCallback: action.payload?.createCallback,
      };
    },
    setFormData: (state, action: PayloadAction<Account>) => {
      // Update form data in redux whenever form changes
      return {
        ...state,
        formData: action.payload,
        stateIsChanged: true,
      };
    },
    closeModal: (
      state,
      action: PayloadAction<{ resetState?: boolean } | undefined>,
    ) => {
      if (action.payload?.resetState) return { ...initialState };
      return {
        ...state,
        isOpenAccountModal: false,
        deleteAccountModal: false,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDisableButtons: (state, action: PayloadAction<boolean>) => {
      state.disableButtons = action.payload;
    },
  },
});

export const {
  openAccountModal,
  closeModal,
  setLoading,
  setDisableButtons,
  setFormData,
} = accountModalSlice.actions;

// Thunks
export const fetchAccount =
  () =>
  async (
    dispatch: Dispatch,
  ): Promise<[FetchUserAttributesOutput | null, unknown]> => {
    try {
      dispatch(setLoading(true));
      // const authUser: AuthUser | undefined = await getUser()(dispatch);
      const user: FetchUserAttributesOutput = await fetchUser();
      return [user, undefined];
    } catch (err) {
      return [null, err];
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateAccount =
  (payload: UpdateUserAttributesInput) =>
  async (
    dispatch: Dispatch,
  ): Promise<[UpdateUserAttributesOutput | null, unknown]> => {
    try {
      dispatch(setLoading(true));
      const user: UpdateUserAttributesOutput = await updateUser(payload);
      return [user, undefined];
    } catch (err) {
      return [null, err];
    } finally {
      dispatch(setLoading(false));
    }
  };

export const changeAccountPassword =
  (payload: UpdatePasswordInput) =>
  async (dispatch: Dispatch): Promise<unknown> => {
    try {
      dispatch(setLoading(true));
      await changePassword(payload);
      return undefined;
    } catch (err) {
      return err;
    } finally {
      dispatch(setLoading(false));
    }
  };

export default accountModalSlice.reducer;
